import { useMemo } from 'react';
import { ProgressBar, VoiceWarning } from '../components/common';
import { useT } from '../hooks/useT';
import { computeStats, needsPractice, getEntry } from '../services/progress';
import { useBank } from '../state/BankContext';
import { useProgress } from '../state/ProgressContext';
import { useRouter } from '../state/RouterContext';
import { useSettings } from '../state/SettingsContext';

interface TileProps {
  icon: string;
  label: string;
  sub?: string;
  primary?: boolean;
  badge?: string;
  onClick: () => void;
}

function Tile({ icon, label, sub, primary, badge, onClick }: TileProps) {
  return (
    <button type="button" className={`tile${primary ? ' tile--primary' : ''}`} onClick={onClick}>
      <span className="tile__icon" aria-hidden="true">
        {icon}
      </span>
      <span className="tile__label">
        {label}
        {sub ? <span className="tile__sub">{sub}</span> : null}
      </span>
      {badge ? <span className="badge badge--practice">{badge}</span> : null}
      <span className="tile__chev" aria-hidden="true">
        ›
      </span>
    </button>
  );
}

export function HomeScreen() {
  const t = useT();
  const { navigate } = useRouter();
  const { settings, uiLang } = useSettings();
  const { questions, signs } = useBank();
  const { progress } = useProgress();

  const stats = useMemo(() => computeStats(progress, questions, signs), [progress, questions, signs]);
  const mistakeCount = useMemo(
    () => questions.filter((q) => needsPractice(getEntry(progress, 'question', q.id))).length,
    [questions, progress],
  );

  return (
    <>
      <header className="app-header">
        <div className="app-header__inner">
          <h1 className="app-header__title">
            <span aria-hidden="true">🚗 </span>
            {uiLang === 'ar' ? 'رخصة أوهايو بالعربي' : 'Ohio Permit Arabic'}
            <span className="app-header__sub">{t('appTagline')}</span>
          </h1>
          <button
            type="button"
            className="btn btn--quiet"
            aria-label={t('settings')}
            onClick={() => navigate({ name: 'settings' })}
          >
            <span className="btn__icon" aria-hidden="true">
              ⚙️
            </span>
          </button>
        </div>
      </header>

      <main id="main" className="app-main stack">
        <h2 style={{ fontSize: '1.6rem', margin: 0 }}>{t('welcome')}</h2>

        <VoiceWarning />

        <div className="card card--flat">
          <ProgressBar
            value={stats.learned}
            max={stats.totalQuestions}
            label={t('questionsLearned')}
            tone="success"
          />
        </div>

        <nav className="tile-grid" aria-label={t('appName')}>
          <Tile
            icon="▶"
            label={t('todayPractice')}
            sub={uiLang === 'ar' ? '10 أسئلة' : '10 questions'}
            primary
            onClick={() => navigate({ name: 'quiz', mode: 'daily' })}
          />
          <Tile icon="🎓" label={t('learn')} onClick={() => navigate({ name: 'quiz', mode: 'learn' })} />
          <Tile
            icon="🎧"
            label={t('listenAndAnswer')}
            onClick={() => navigate({ name: 'quiz', mode: 'listen' })}
          />
          <Tile icon="🛑" label={t('roadSigns')} onClick={() => navigate({ name: 'signs' })} />
          <Tile
            icon="🔁"
            label={t('myMistakes')}
            badge={mistakeCount > 0 ? String(mistakeCount) : undefined}
            onClick={() => navigate({ name: 'quiz', mode: 'mistakes' })}
          />
          <Tile icon="📝" label={t('practiceTest')} onClick={() => navigate({ name: 'quiz', mode: 'test' })} />
        </nav>

        <div className="tile-grid tile-grid--two">
          <Tile icon="📚" label={t('byTopic')} onClick={() => navigate({ name: 'categories' })} />
          <Tile icon="📊" label={t('myProgress')} onClick={() => navigate({ name: 'progress' })} />
        </div>

        {!settings.easyMode ? (
          <p style={{ textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.8rem' }}>
            {t('privacyNote')}
          </p>
        ) : null}
      </main>
    </>
  );
}
